import { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import type { BusinessSettings } from '../types';
import { withBusinessSystem } from '../utils/tradingSystem';

const FALLBACK: BusinessSettings = {
  businessId: '',
  currency: 'USD',
  certifiedPrefix: 'PC',
  loosePrefix: 'PL',
  looseColorOptions: [],
  looseClarityOptions: [],
};

/** True once the company's own settings have arrived (until then the
 *  fallback above is returned, e.g. currency USD). */
export function settingsLoaded(s: BusinessSettings): boolean {
  return s !== FALLBACK;
}

export function useSettings(): BusinessSettings {
  const { business } = useAuth();
  const [settings, setSettings] = useState<BusinessSettings>(FALLBACK);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const unsub = onSnapshot(
      doc(db, 'settings', business.id),
      snap => { if (snap.exists()) setSettings({ ...FALLBACK, ...(snap.data() as BusinessSettings) }); },
    );
    return unsub;
  }, [business]);

  // The Trading System is set in the Admin Portal (company record).
  // FALLBACK itself is returned untouched so settingsLoaded still works.
  return useMemo(
    () => settings === FALLBACK ? settings : withBusinessSystem(settings, business),
    [settings, business],
  );
}
